import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import * as FileSaver from 'file-saver';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, MonoTypeOperatorFunction, Observable, of, Subscription } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { APP_CONFIG } from '../app.config';
import { AntragService } from '../common/antrag.service';
import { DateFormatter, DateUtilsService } from '../common/date-utils.service';
import { EncryptSignSaveService } from '../common/encryptsignsave.service';
import { LanguageService } from '../common/language.service';
import { Marshaller } from '../common/marshaller';
import { MarshallerFactory } from '../common/marshaller-factory';
import { UserSettingsService } from '../common/usersettings.service';
import { ValueListsService } from '../common/value-lists.service';
import { AntragNachDownload } from '../models/antrag.nach.download';
import { AppConfig } from '../models/config/app-config';
import { VisumAntrag } from '../models/visum.antrag';
import { VisumAntragZumSpeichern } from '../models/visum.antrag.zum.speichern';


@Component({
  selector: 'app-visum-form',
  templateUrl: './visum-form.component.html',
  styleUrls: ['./visum-form.component.scss']
})

export class VisumFormComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('antragOeffnenFehler', { static: true })
  antragOeffnenFehler: ElementRef;

  @ViewChild('mekFehler', { static: true })
  mekFehler: ElementRef;

  @ViewChild('validationErrorsDialog', { static: true })
  validationErrorsDialog: ElementRef;

  @ViewChild('unknownErrorDialog', { static: true })
  unknownErrorDialog: ElementRef;

  @ViewChild('antragViewer', { static: true })
  antragViewer: ElementRef;

  // Hier wird jeweils eine temporäre URL für das gerade anzuzeigende Antrag-PDF gespeichert
  antragViewerUrl: any;

  modelUpdate$ = new BehaviorSubject({});

  formValue: any = {};

  pruefblattScreen = false;

  validationErrors: any;

  antragOeffnenFehlermeldungen: string[] = [];

  validities: { [key: string]: boolean };

  disableButtons = false;

  marshaller: Marshaller;

  private antragZumSpeichern: VisumAntragZumSpeichern = Object.assign({});

  private dateFormatter: DateFormatter;

  private subscription$: Subscription = new Subscription();
  private avSchluessel?: string;

  constructor(
    private languageService: LanguageService,
    private valueListsService: ValueListsService,
    private encryptSignSaveFileService: EncryptSignSaveService,
    private antragService: AntragService,
    private userSettingsService: UserSettingsService,
    private modalService: NgbModal,
    private sanitizer: DomSanitizer,
    dateUtils: DateUtilsService,
    readonly spinnerService: NgxSpinnerService,
    private marshallerFactory: MarshallerFactory,
    private activatedRoute: ActivatedRoute,
    @Inject(APP_CONFIG) private appConfig: AppConfig
  ) {
    this.dateFormatter = dateUtils.getFormatter('yMd');
    this.marshaller = this.marshallerFactory.createMarshaller(this.valueListsService, this.dateFormatter, this.appConfig);
    this.userSettingsService.avSchluessel$.pipe(take(1)).subscribe(avSchluessel => this.avSchluessel = avSchluessel);
  }

  ngOnInit(): void {
    if (this.isMek()) {
      this.doInitialChecksAndSetup();
    }
  }

  private doInitialChecksAndSetup(): void {
    this.antragZumSpeichern = this.createAntragZumSpeichernNachOeffnen();
    this.subscription$.add(
      this.retrieveAntragFromDownloadCache().pipe(
        this.tapAndLoadAntragIntoForm(),
        catchError((error: HttpErrorResponse): Observable<any> => {
          this.antragOeffnenFehlermeldungen.push(error.error);
          return of(null);
        }),
        this.tapAndShowErrorMessageIfNecessary()
      ).subscribe()
    );
  }

  private createAntragZumSpeichernNachOeffnen(): VisumAntragZumSpeichern {
    return {
      avSchluessel: this.avSchluessel
    } as VisumAntragZumSpeichern;
  }

  /**
   * Lädt einen Antrag aus dem Antrag-Download-Cache (der Cache in dem ein Antrag nach dem Download aus
   * dem Auslandsportal (auch bekannt als Customer Service Portal - kurz CSP) zwischengespeichert wird),
   * falls eine Anfrage zum Laden eines Antrages existiert.
   */
  private retrieveAntragFromDownloadCache(): Observable<AntragNachDownload> {
    const antragId: string = this.activatedRoute.snapshot.queryParamMap.get('antragId');
    if (!antragId || !this.isLangfristigerAufenthalt()) {
      return of(null);
    }
    const sprache = this.languageService.getLanguage();
    return this.antragService.retrieveAntragFromDownloadCache(antragId, sprache);
  }

  private tapAndLoadAntragIntoForm(): MonoTypeOperatorFunction<AntragNachDownload> {
    return tap(
      (antragNachDownload) => {
        if (antragNachDownload) {
          this.antragZumSpeichern = this.createAntragZumSpeichernNachDownload(antragNachDownload);
          const formValue = this.marshaller.unmarshall(antragNachDownload.visumAntrag);
          this.onLoad(formValue);
        }
      }
    );
  }

  private createAntragZumSpeichernNachDownload(antragNachDownload: AntragNachDownload): VisumAntragZumSpeichern {
    const antragId: string = this.activatedRoute.snapshot.queryParamMap.get('antragId');
    return {
      avSchluessel: antragNachDownload.avSchluessel,
      antragId: antragId
    } as VisumAntragZumSpeichern;
  }

  private tapAndShowErrorMessageIfNecessary(): MonoTypeOperatorFunction<string> {
    return tap(
      () => {
        if (0 < this.antragOeffnenFehlermeldungen.length) {
          this.modalService.open(this.antragOeffnenFehler);
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  onFormChange(formValue: any) {
    this.setFormValue(formValue);
  }

  onSave() {
    this.spinnerService.show();
    try {
      if (this.appConfig.umgebung !== 'MEK') {
        this.saveNotMek();
      } else {
        this.saveMek();
      }
    } catch (e) {
      console.log(e);
      this.spinnerService.hide();
    }
  }

  onLoad(model: any) {
    this.setFormValue(model);
    this.modelUpdate$.next(model);
  }

  onComplete() {
    if (this.appConfig.umgebung !== 'MEK') {
      if (this.antragViewerUrl) {
        this.showAntragPopup();
      } else {
        this.spinnerService.show();
        this.disableButtons = true;
        this.antragService.getPdf(
          this.marshaller.marshallAlphanumericOnly(this.formValue),
          this.languageService.getLanguage()
        ).subscribe(
          {
            next: (response: HttpResponse<Blob>) => {
              this.showAntragPopup(response.body);
            },
            error: (error) => this.handleError(error)
          }
        ).add(() => {
          this.disableButtons = false;
          this.spinnerService.hide();
        });
      }
    }
  }

  onCancel() {
    //navigate to startseite
    window.location.href = location.origin;
  }

  private handleError(errorResponse: HttpErrorResponse): void {
    if (errorResponse.status === 429 || errorResponse.status === 403) {
      this.modalService.open(this.unknownErrorDialog).result.then().catch();
      return;
    }
    if (errorResponse.headers.get('Content-Type') && errorResponse.headers.get('Content-Type').indexOf('application/json') >= 0) {
      const reader = new FileReader();
      reader.onload = (_e) => {
        this.validationErrors = JSON.parse(reader.result as string) || undefined;
        if (this.validationErrors) {
          this.modalService.open(this.validationErrorsDialog).result.then((_result) => this.validationErrors = undefined);
        }
      };
      reader.readAsText(errorResponse.error);
      Object.keys({});
    } else {
      console.log('Content ist kein json!');
    }
  }

  onPrintPruefblatt() {
    this.spinnerService.show();
    this.antragService.printPdf(
      this.marshaller.marshallAlphanumericOnly(this.formValue),
      this.languageService.getLanguage()
    ).subscribe(
      {
        next: () => {
          console.log('Druckauftrag gesendet');
        },
        error: () => {
          console.log('Druckauftrag NICHT gesendet');
        }
      }
    ).add(() => {
      this.spinnerService.hide();
    });
  }

  onReset() {
    this.setFormValue({});
    this.modelUpdate$.next({});
    this.scrollToTopOfPage();
  }

  private scrollToTopOfPage() {
    const scrollToOptions: ScrollToOptions = {
      top: 0,
      left: 0,
      behavior: 'smooth'
    };

    setTimeout(() => {
      window.scrollTo(scrollToOptions);
    }, 250);
  }


  updateValidities(validities: { [key: string]: boolean }) {
    this.validities = validities;
  }

  private saveNotMek() {
    const backendObject = this.marshaller.marshall(this.formValue);
    const blob = new Blob([JSON.stringify(backendObject)], { type: 'application/json' });
    const filename: string = this.createFilename();
    FileSaver.saveAs(blob, filename);
    this.spinnerService.hide();
  }

  private saveMek() {
    const visumAntrag = this.marshaller.marshall(this.formValue) as VisumAntrag;
    this.antragZumSpeichern.visumAntrag = visumAntrag;
    this.antragZumSpeichern.passnummer = visumAntrag.antragsteller.pass.passnummer;
    this.encryptSignSaveFileService.saveFile(
      this.antragZumSpeichern,
      this.languageService.getLanguage()
    ).subscribe(
      {
        next: () => {
          this.pruefblattScreen = true;
        },
        error: (error: HttpErrorResponse) => {
          this.validationErrors = error.error;
          this.modalService.open(this.mekFehler).result.then(() => {
            this.validationErrors = undefined;
            this.pruefblattScreen = false;
          });
        }
      }
    ).add(() => {
      this.spinnerService.hide();
    });
  }

  private createFilename(): string {
    const now = new Date();

    return 'Visumantrag_'
      + (this.formValue.angabenZurPerson.familienname ? this.formValue.angabenZurPerson.familienname : 'UNBEKANNT')
      + '_'
      + now.getFullYear()
      + (now.getMonth() < 9 ? '0' : '') + (now.getMonth() + 1)
      + (now.getDate() < 10 ? '0' : '') + now.getDate()
      + '-'
      + (now.getHours() < 10 ? '0' : '') + now.getHours()
      + (now.getMinutes() < 10 ? '0' : '') + now.getMinutes()
      // + (now.getSeconds() < 10 ? '0' : '') + now.getSeconds()
      + '.json';
  }

  private showAntragPopup(antragBlob?: Blob): void {
    if (antragBlob) {
      this.antragService.latestPdfUrl = URL.createObjectURL(antragBlob);
      this.antragViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.antragService.latestPdfUrl);
    }
    this.modalService.open(this.antragViewer, { size: 'lg' }).result.then(
      () => {
        //This is called when the modal is closed, by clicking the "x"
        this.handleModalClosed();
      },
      () => {
        //This is called when the modal is closed, by clicking outside the modal
        this.handleModalClosed();
      }
    ).catch(() => {
    });
  }

  private handleModalClosed() {
    if (this.antragService.latestPdfUrl && this.antragService.pdfOpened) {
      URL.revokeObjectURL(this.antragService.latestPdfUrl);
      this.antragService.latestPdfUrl = null;
    }
  }

  private isMek(): boolean {
    return this.appConfig.umgebung === 'MEK';
  }

  private isLangfristigerAufenthalt(): boolean {
    return this.appConfig.visumAntragsartEnum === 'LANGFRISTIGER_AUFENTHALT';
  }

  private setFormValue(formValue) {
    this.formValue = formValue;
    this.antragViewerUrl = null;
  }

  ngAfterViewInit(): void {
    this.setFormValue({});
    this.modelUpdate$.next({});
  }

  onPdfClick(): void {
    this.antragService.pdfOpened = true;
  }
}
